import {
  Card,
  CardContent,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  Typography
} from '@mui/material'
import Grid from '@mui/material/Grid'
import TableContainer from '@mui/material/TableContainer'
import TablePagination from '@mui/material/TablePagination'
import { ExplanationAccordion, SortableTableCell } from 'components'
import * as controllers from 'controllers'
import { VehicleBondCommunicationDto, VehicleDto } from 'dtos'
import { VehicleBondCommunicationParameters } from 'parameters'
import { useEffect, useState } from 'react'
import { formatDateTime } from 'utils'

interface BondEmailSentListCardProps {
  vehicle: VehicleDto
}

export default function BondEmailSentListCard({ vehicle }: BondEmailSentListCardProps) {
  const [isGettingVehicleBondCommunications, setIsgettingVehicleBondCommunications] = useState<boolean>(false)
  const [vehicleBondCommunications, setVehiclebondCommunications] = useState<VehicleBondCommunicationDto[]>([])
  const [totalCount, setTotalCount] = useState<number>(0)
  const [parameters, setParameters] = useState<VehicleBondCommunicationParameters>({
    page: 0,
    pageSize: 5,
    orderBy: 'sentByPerson',
    order: 'desc'
  })

  const onChangeSortByDirection = (orderBy: string) => {
    setParameters(prev => ({
      ...parameters,
      orderBy,
      order: prev.orderBy === orderBy && prev.order === 'asc' ? 'desc' : 'asc'
    }))
  }

  useEffect(() => {
    if (vehicle.vehicleGuid) {
      setIsgettingVehicleBondCommunications(true)
      controllers
        .getVehicleBondCommunications(vehicle.vehicleGuid, parameters)
        .then(response => {
          setVehiclebondCommunications(response.value)
          setTotalCount(response.totalCount!)
        })
        .finally(() => {
          setIsgettingVehicleBondCommunications(false)
        })
    }
  }, [vehicle.vehicleGuid, parameters])

  return (
    <Card variant='outlined'>
      <CardContent>
        <Grid container spacing={1} alignItems='flex-start'>
          <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
            <Grid item xs={12} sm='auto'>
              <Typography fontWeight='bold' variant='h5'>
                Bond Communications
              </Typography>
            </Grid>

            {isGettingVehicleBondCommunications && (
              <Grid item container alignItems='center' justifyContent='center' sx={{ my: 1 }}>
                <Grid>
                  <CircularProgress color='primary' size={50} />
                </Grid>
              </Grid>
            )}

            {!isGettingVehicleBondCommunications && (
              <Grid item xs={12}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <SortableTableCell
                          onChangeSortByDirection={onChangeSortByDirection}
                          propertyName='sentByPerson'
                          sortBy={parameters.orderBy}
                          sortDirection={parameters.order}
                        >
                          User
                        </SortableTableCell>

                        <SortableTableCell
                          onChangeSortByDirection={onChangeSortByDirection}
                          propertyName='sentDateTime'
                          sortBy={parameters.orderBy}
                          sortDirection={parameters.order}
                        >
                          Sent Date
                        </SortableTableCell>

                        <SortableTableCell
                          onChangeSortByDirection={onChangeSortByDirection}
                          propertyName='sentToEmail'
                          sortBy={parameters.orderBy}
                          sortDirection={parameters.order}
                        >
                          Sent To
                        </SortableTableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {vehicleBondCommunications?.map(vehicleBondCommunication => (
                        <TableRow>
                          <TableCell>
                            {vehicleBondCommunication.sentByPerson?.firstName} {vehicleBondCommunication.sentByPerson?.lastName}
                          </TableCell>

                          <TableCell align='left'>
                            {vehicleBondCommunication.sentDateTime ? formatDateTime(vehicleBondCommunication.sentDateTime) : ''}
                          </TableCell>

                          <TableCell align='left'>{vehicleBondCommunication.sentToEmail}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>

                    <TableFooter>
                      <TableRow>
                        <TablePagination
                          count={totalCount}
                          onPageChange={(_, page) => {
                            setParameters({ ...parameters, page })
                          }}
                          onRowsPerPageChange={e => {
                            setParameters({ ...parameters, page: 0, pageSize: e.target.value as unknown as number })
                          }}
                          page={parameters.page}
                          rowsPerPage={parameters.pageSize}
                          rowsPerPageOptions={[5, 10, 25, 50, 100]}
                          SelectProps={{ fullWidth: false }}
                        />
                      </TableRow>
                    </TableFooter>
                  </Table>
                </TableContainer>
              </Grid>
            )}

            <Grid item xs={12}>
              <ExplanationAccordion variant='outlined'>Helpful Text Coming Soon.</ExplanationAccordion>
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}
